import {coreObjectArchiveAction} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  MAX_PARAMETER_DESCRIPTION_LENGTH,
  MAX_PARAMETER_NAME_LENGTH,
} from 'procedures/design/ProcedureDesignConstants';
import {getParameterNameSqlIdentifierErrorMessage} from 'procedures/design/validation/private-validation-helpers/validateParameters';

import {LibraryParameterDefinition, LibraryParameterDefinitionDataType} from '../ParameterLibraryTypes';

/** @package */
export const LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME: Record<
  LibraryParameterDefinitionDataType,
  string
> = {
  [FieldDefinitionType.FLOAT]: 'Decimal',
  [FieldDefinitionType.TEXT]: 'Text',
  [FieldDefinitionType.DATETIME]: 'Datetime',
};

/** Handles generating error message for library parameter definition name.
 * Some of the cases that are handled:
 * - Name is too long
 * - Name is already in use
 * - Name cannot become a valid SQL identifier
 *
 * @param name The name to validate.
 * @param existingNames A set of all existing names in the library.
 * @param existingArchivedNames A set of all archived names in the library.
 * @param originalName When editing an existing definition, pass its current name so the
 *   duplicate check doesn't flag the definition's own name as a conflict.
 */
export function getParameterNameErrorMessage(
  name: string,
  existingNames: Set<string>,
  existingArchivedNames: Set<string>,
  originalName?: string
): string | null {
  if (name.length > MAX_PARAMETER_NAME_LENGTH) {
    return 'Name is too long';
  }

  const nameAlreadyInUse = existingNames.has(name) && name !== originalName;
  if (nameAlreadyInUse) {
    return existingArchivedNames.has(name)
      ? 'Name already in use by archived parameter'
      : 'Name already in use';
  }
  return getParameterNameSqlIdentifierErrorMessage(name, 'Parameter');
}

export function getDescriptionErrorMessage(description: string): string | null {
  return description.length > MAX_PARAMETER_DESCRIPTION_LENGTH ? 'Description is too long' : null;
}

export type ParameterDefinitionEditMode = 'none' | 'create' | 'edit';

export interface ParameterDefinitionValidationResult {
  nameErrorMessage: string | null;
  descriptionErrorMessage: string | null;
  unitTypeErrorMessage: string | null;
  isValid: boolean;
}

interface ValidateParameterDefinitionDraftChangesArgs {
  editMode: ParameterDefinitionEditMode;
  trimmedName: string;
  trimmedDescription: string;
  dataType: string;
  unitTypeId: string | null;
  existingNames: Set<string>;
  existingArchivedNames: Set<string>;
  originalName: string;
  originalDescription: string;
}

/**
 * Validates a parameter definition draft and returns field-level error messages
 * plus a isValid flag indicating whether the changes are valid to save.
 *
 * Behavior varies by editMode:
 * - 'create': requires unit type for decimal parameters; any non-empty valid
 *   name is sufficient (no meaningful-change check).
 * - 'edit': requires at least one meaningful change (name or description)
 *   from the original values; skips unit type validation since data type
 *   is immutable after creation.
 * - 'none': short-circuits to isValid: false with no error messages.
 */
export function validateParameterDefinitionDraftChanges({
  editMode,
  trimmedName,
  trimmedDescription,
  dataType,
  unitTypeId,
  existingNames,
  existingArchivedNames,
  originalName,
  originalDescription,
}: ValidateParameterDefinitionDraftChangesArgs): ParameterDefinitionValidationResult {
  if (editMode === 'none') {
    return {
      nameErrorMessage: null,
      descriptionErrorMessage: null,
      unitTypeErrorMessage: null,
      isValid: false,
    };
  }
  const isCreating = editMode === 'create';
  const nameErrorMessage = getParameterNameErrorMessage(
    trimmedName,
    existingNames,
    existingArchivedNames,
    isCreating ? undefined : originalName
  );
  const descriptionErrorMessage = getDescriptionErrorMessage(trimmedDescription);
  const unitTypeErrorMessage =
    isCreating && dataType === FieldDefinitionType.FLOAT && unitTypeId == null
      ? 'Unit type is required for decimal-type parameters'
      : null;
  const hasMeaningfulChanges =
    trimmedName !== originalName.trim() || trimmedDescription !== originalDescription.trim();
  const isValid =
    trimmedName !== '' &&
    nameErrorMessage == null &&
    descriptionErrorMessage == null &&
    unitTypeErrorMessage == null &&
    (isCreating || hasMeaningfulChanges);

  return {nameErrorMessage, descriptionErrorMessage, unitTypeErrorMessage, isValid};
}

export function archiveParameterDefinition(parameterDefinition: LibraryParameterDefinition): void {
  ArchiveModal.open({
    items: [parameterDefinition],
    onArchive: async (itemIds: Array<string>, purpose: string): Promise<void> => {
      await coreObjectArchiveAction(itemIds, purpose);
    },
  });
}

export async function unarchiveParameterDefinition(
  parameterDefinition: LibraryParameterDefinition
): Promise<void> {
  const unarchiveFn = getUnarchiveAction([parameterDefinition.id]);
  await unarchiveFn([parameterDefinition.id]);
}
